import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { TIPOS_DOCUMENTO, labelTipoDocumento } from "../utils/societarioCalculos";
import { estiloBotaoAcao, estiloBotaoAdicionar, estiloCabecalhoInterno, estiloCelulaInterna, estiloSelectInline } from "../utils/societarioEstilos";

// ==========================================================
// ABA DOCUMENTOS (GED): minuta, assinado e registrado.
// Os arquivos são enviados ao servidor e ficam disponíveis a
// todos os usuários com acesso à tela, de qualquer lugar.
// Em desenvolvimento local, os anexos de teste ficam somente
// na máquina de desenvolvimento (marcados como "Teste local").
// ==========================================================

const dataHora = (v) => (v ? new Date(v).toLocaleString("pt-BR") : "—");
const tamanho = (bytes) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const EXTENSOES = /\.(pdf|doc|docx|jpg|jpeg|png)$/i;
const TAMANHO_MAXIMO = 20 * 1024 * 1024;

export default function SocietarioDocumentos({ documentos, alteracaoSalva, podeAnexar, etapaAtual, nomeEtapa, onAnexar, onRemover, onVisualizar }) {
  const [tipo, setTipo] = useState("");
  const [erro, setErro] = useState("");
  const [enviando, setEnviando] = useState(false);
  const inputArquivoRef = useRef(null);

  const selecionarArquivo = () => {
    if (!tipo) return setErro("Selecione o tipo do documento antes de escolher o arquivo.");
    setErro("");
    inputArquivoRef.current?.click();
  };

  const anexar = async (evento) => {
    const arquivo = evento.target.files?.[0];
    evento.target.value = "";
    if (!arquivo) return;
    if (!EXTENSOES.test(arquivo.name)) return setErro("Envie um arquivo PDF, DOC, DOCX, JPG ou PNG.");
    if (arquivo.size > TAMANHO_MAXIMO) return setErro("Arquivo maior que 20 MB.");
    setEnviando(true);
    try {
      await onAnexar(tipo, arquivo);
      setTipo("");
      setErro("");
    } catch (e) {
      setErro(e.message);
    } finally {
      setEnviando(false);
    }
  };

  const executar = async (acao) => {
    try {
      setErro("");
      await acao();
    } catch (e) {
      setErro(e.message);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Alert severity="info">
        Para avançar de etapa, é necessário ao menos um documento anexado na etapa atual. Arquivos aceitos: PDF, DOC, DOCX, JPG e PNG (até 20 MB).
      </Alert>
      {!alteracaoSalva && <Alert severity="warning">Salve a alteração para poder anexar documentos.</Alert>}

      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ width: "100%", minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={estiloCabecalhoInterno}>Tipo</TableCell>
              <TableCell sx={{ ...estiloCabecalhoInterno, width: "40%" }}>Arquivo</TableCell>
              <TableCell sx={estiloCabecalhoInterno}>Etapa</TableCell>
              <TableCell sx={estiloCabecalhoInterno}>Enviado em</TableCell>
              <TableCell sx={estiloCabecalhoInterno}>Usuário</TableCell>
              <TableCell sx={{ ...estiloCabecalhoInterno, textAlign: "right" }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documentos.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  sx={{ borderBottom: "1px solid var(--ig-b-333333)", color: "var(--ig-t-666666)", fontStyle: "italic", py: 1.5, textAlign: "center" }}
                >
                  Nenhum documento anexado.
                </TableCell>
              </TableRow>
            )}
            {documentos.map((d) => (
              <TableRow key={d.id} sx={{ "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" } }}>
                <TableCell sx={estiloCelulaInterna}>{labelTipoDocumento(d.tipo)}</TableCell>
                <TableCell sx={estiloCelulaInterna}>
                  {d.nomeArquivo} <span style={{ color: "var(--ig-t-888888)" }}>({tamanho(d.tamanho)})</span>
                  {d.armazenamento === "LOCAL" && (
                    <Tooltip title="Anexo de teste salvo somente na máquina de desenvolvimento; não é enviado ao servidor.">
                      <Chip size="small" label="Teste local" sx={{ ml: 1, backgroundColor: "#ed6c02", color: "#fff" }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell sx={{ ...estiloCelulaInterna, color: "var(--ig-t-cccccc)" }}>{nomeEtapa(d.etapaId)}</TableCell>
                <TableCell sx={{ ...estiloCelulaInterna, color: "var(--ig-t-cccccc)", whiteSpace: "nowrap" }}>{dataHora(d.enviadoEm)}</TableCell>
                <TableCell sx={{ ...estiloCelulaInterna, color: "var(--ig-t-cccccc)" }}>{d.usuarioNome || "—"}</TableCell>
                <TableCell align="right" sx={{ ...estiloCelulaInterna, whiteSpace: "nowrap" }}>
                  <Tooltip title="Visualizar documento">
                    <IconButton size="small" onClick={() => executar(() => onVisualizar(d))} sx={estiloBotaoAcao("#1998a8", "#1565c0")}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {podeAnexar && (
                    <Tooltip title="Remover documento">
                      <IconButton size="small" onClick={() => executar(() => onRemover(d))} sx={estiloBotaoAcao("#d32f2f", "#c62828")}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {podeAnexar && (
              <TableRow sx={{ backgroundColor: "var(--ig-f-1a1a1a)" }}>
                <TableCell colSpan={6} sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.8 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "flex-end" }}>
                    <Typography sx={{ color: "var(--ig-t-9ca3af)", fontSize: "0.85rem", mr: "auto" }}>Etapa do anexo: {nomeEtapa(etapaAtual)}</Typography>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <Select value={tipo} displayEmpty onChange={(e) => setTipo(e.target.value)} sx={estiloSelectInline}>
                        <MenuItem value="">
                          <em>Tipo do documento</em>
                        </MenuItem>
                        {TIPOS_DOCUMENTO.map((t) => (
                          <MenuItem key={t.value} value={t.value}>
                            {t.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button size="small" startIcon={<UploadFileIcon />} onClick={selecionarArquivo} disabled={enviando} sx={estiloBotaoAdicionar}>
                      {enviando ? "Enviando..." : "Anexar arquivo"}
                    </Button>
                    <input ref={inputArquivoRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" hidden onChange={anexar} />
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      {erro && <Typography sx={{ color: "#f44336", fontSize: "0.8rem" }}>{erro}</Typography>}
    </Box>
  );
}
